<template lang="pug">
    section.shipping-step(v-if="form.purchaser.shippingAddress")
        .hidden
            el-form-item(prop="purchaser.shippingAddress.street")
            el-form-item(prop="purchaser.shippingAddress.zipcode")
            el-form-item(prop="purchaser.shippingAddress.city")
            el-form-item(prop="purchaser.shippingAddress.country")

        .flex-row
            el-form-item(
                :label="$t('Shipping Method')"
                prop="shippingMethod"
                :class="{ grow: !shippingMethod || shippingMethod.id !== 'custom' }"
            )
                el-select.is-full-width(
                    clearable
                    v-model="shippingMethod"
                    valueKey="id"
                )
                    el-option(
                        v-for="(item, index) in shippingMethods"
                        :key="item.id"
                        :label="item.name"
                        :value="item"
                    )
                        .custom-option
                            .flex-row
                                span {{ item.name }}
                                span.price(v-if="item.price") {{ item.price | moneyFormat(currency.code) }}
                            small.description(v-if="item.description") {{ item.description }}
                    el-option(
                        key="custom"
                        :label="$t('Custom Shipping Method')"
                        :value="customerShippingMethod"
                    )
            el-form-item.grow(
                v-if="shippingMethod && shippingMethod.id === 'custom'"
                :label="$t('Shipping Method Name')"
                prop="shippingMethod.name"
            )
                el-input(v-model="shippingMethod.name")
            el-form-item(
                v-if="shippingMethod && shippingMethod.id === 'custom'"
                :label="$t('Shipping Fee')"
            )
                el-input(type="number" v-model="customShipipngFee" step="0.01" min="0")
                    template(slot="append")
                        i.el-icon-fa-euro-sign.fas(v-if="currency.sign === '€'")
                        i.el-icon-fa-dollar-sign.fas(v-else-if="currency.sign === '$'")
                        b.currency(v-else) {{ currency.sign }}

        h3 {{ $t('Shipping Address') }}

        .personal-data
            .flex-row
                el-form-item(:label="$t('Salutation')" prop="purchaser.shippingAddress.gender")
                    el-select.is-full-width(
                        placeholder=""
                        clearable
                        v-model="form.purchaser.shippingAddress.gender"
                        :placeholder="getSalutation(form.purchaser.gender)"
                    )
                        el-option(:label="$t('Mr.')" value="M")
                        el-option(:label="$t('Mrs.')" value="F")
                        el-option(:label="$t('Undefined')" value="X")
                el-form-item.grow(:label="$t('First name')" prop="purchaser.shippingAddress.first_name")
                    el-input(
                        v-model="form.purchaser.shippingAddress.first_name"
                        :placeholder="form.purchaser.first_name"
                        maxlength="255"
                        show-word-limit
                    )
                el-form-item.grow(:label="$t('Last name')" prop="purchaser.shippingAddress.last_name")
                    el-input(
                        v-model="form.purchaser.shippingAddress.last_name"
                        :placeholder="form.purchaser.last_name"
                        maxlength="255"
                        show-word-limit
                    )
            .flex-row
                el-form-item.grow(:label="$t('Email')" prop="purchaser.shippingAddress.email")
                    el-input(
                        v-model="form.purchaser.shippingAddress.email"
                        :placeholder="form.purchaser.email"
                        maxlength="255"
                        show-word-limit
                    )
                el-form-item.grow(:label="$t('Phone')" prop="purchaser.shippingAddress.phone")
                    el-input(
                        v-model="form.purchaser.shippingAddress.phone"
                        :placeholder="form.purchaser.phone"
                        maxlength="255"
                        show-word-limit
                    )

        .shipping-address
            el-form-item(:label="$t('Street')" prop="purchaser.shippingAddress.street")
                el-input(v-model="form.purchaser.shippingAddress.street" maxlength="255" show-word-limit)
            el-row.no-padding(:gutter="20")
                el-col(:lg="8" :md="8")
                    el-form-item(:label="$t('Zipcode')" prop="purchaser.shippingAddress.zipcode")
                        el-input(v-model="form.purchaser.shippingAddress.zipcode" maxlength="255" show-word-limit)
                el-col(:lg="16" :md="16")
                    el-form-item(:label="$t('City')" prop="purchaser.shippingAddress.city")
                        el-input(v-model="form.purchaser.shippingAddress.city" maxlength="255" show-word-limit)
            el-form-item.no-margin(:label="$t('Country')" prop="purchaser.shippingAddress.country")
                el-select.is-full-width(
                    placeholder=""
                    filterable
                    v-model="form.purchaser.shippingAddress.country"
                    autocomplete="no-fill"
                )
                    el-option(
                        v-for="(label, value) in countries"
                        :key="value"
                        :label="label"
                        :value="value"
                    )
</template>

<i18n>
{
    "de": {
        "Salutation": "Anrede",
        "First name": "Vorname",
        "Last name": "Nachname",
        "Email": "E-Mail",
        "Password": "Passwort",
        "Phone": "Telefon",
        "Company": "Firma",
        "Mr.": "Herr",
        "Mrs.": "Frau",
        "Undefined": "Unbekannt",
        "Shipping Method": "Versandart",
        "Custom Shipping Method": "Individuelle Versandart",
        "Shipping Method Name": "Versandart Name",
        "Shipping Fee": "Versandkosten",
        "Shipping Address": "Lieferadresse",
        "Street": "Straße",
        "Zipcode": "PLZ",
        "City": "Ort",
        "Country": "Land"
    }
}
</i18n>

<script>
import _ from 'lodash';
import uniqid from 'uniqid';
import { mapGetters } from 'vuex';

import countries from 'i18n-iso-countries'
import en from 'i18n-iso-countries/langs/en.json'
import de from 'i18n-iso-countries/langs/de.json'

countries.registerLocale(en)
countries.registerLocale(de)

import store from '@/store'

export default {
    name: 'ShippingStep',
    props: {
        form: {
            type: Object,
            required: true
        },
        rules: {
            type: Object,
            required: true
        },
        subTotal: {
            type: Number,
            required: true
        },
    },
    data() {
        return {
            customerShippingMethod: {
                id: 'custom',
                name: this.$t('Custom Shipping Method'),
                service: 'custom',
                price: 0,
            }
        }
    },
    computed: {
        shippingMethod: {
            get() {
                return this.form.shippingMethod
            },
            set(value) {
                this.$set(this.form, 'shippingMethod', value)
                if (value.id === 'custom') {
                    this.$set(this.form.shippingMethod, 'price', 0)
                }
            }
        },
        customShipipngFee: {
            get() {
                return this.form.shippingMethod.price
            },
            set(value) {
                this.$set(this.form.shippingMethod, 'price', parseFloat(value))
            }
        },
        shippingMethods() {
            return this.options.shippingMethod.items.filter(method => {
                // if method is not free then check for availability
                // by customer group prices

                if (method.price_type !== 'free') {
                    method.price = this.findPrice(method.prices);
                    if (!method.price) return false;
                }

                const purchaserCountry = _.get(this.form.purchaser, 'shippingAddress.country')
                const purchaserCustomerGroup = _.get(this.form.purchaser, 'customer.customerGroup')
                // const memberTagNames = authStore.member?.memberTags?.map((tag: MemberTag) => tag.name) || []
                const purchaserMemberTags = _.get(this.form.purchaser, 'customer.memberTags') || []
                const purchaserMemberTagNames = purchaserMemberTags.map(tag => tag.name)

                // apply rules
                let allRulesValid = true
                method.shippingMethodRules.forEach(rule => {
                    const memberTags = _.get(rule, 'memberTags') || []
                    const ruleTagNames = memberTags.map(tag => tag.name) || []

                    const countries = _.get(rule, 'countries') || []
                    const customerGroups = _.get(rule, 'customerGroups') || []

                    switch (rule.type) {
                        case 'price':
                            const minSum = _.get(rule, 'min_sum')
                            const maxSum = _.get(rule, 'max_sum')
                            if (!minSum && !maxSum) return

                            let subTotal = this.subTotal
                            const taxFree = _.get(this.form.purchaser, 'customer.tax_free')

                            if (minSum && subTotal < minSum) allRulesValid = false
                            if (maxSum && subTotal > maxSum) allRulesValid = false
                            break

                        case 'points':
                            const minPoints = _.get(rule, 'min_points')
                            const maxPoints = _.get(rule, 'max_points')
                            if (!minPoints && !maxPoints) return

                            if (minPoints && this.shippingPoints < minPoints) allRulesValid = false
                            if (maxPoints && this.shippingPoints > maxPoints) allRulesValid = false
                            break

                        case 'countries:include':
                            if (!countries.length) return;
                            if (!countries.includes(purchaserCountry)) allRulesValid = false
                            break

                        case 'countries:exclude':
                            if (!countries.length) return;
                            if (countries.includes(purchaserCountry)) allRulesValid = false
                            break

                        case 'customer-groups:include':
                            if (!customerGroups.length) return
                            if (!purchaserCustomerGroup || !customerGroups.some(cg => cg.id === purchaserCustomerGroup.id)) {
                                allRulesValid = false
                            }
                            break

                        case 'customer-groups:exclude':
                            if (!customerGroups.length) return
                            if (!purchaserCustomerGroup || customerGroups.some(cg => cg.id === purchaserCustomerGroup.id)) {
                                allRulesValid = false
                            }
                            break

                        case 'member-tags:include':
                            if (!memberTags.length) return
                            if (!purchaserMemberTagNames.some(name => ruleTagNames.includes(name))) allRulesValid = false
                            break

                        case 'member-tags:exclude':
                            if (!memberTags.length) return
                            if (purchaserMemberTagNames.some(name => ruleTagNames.includes(name))) allRulesValid = false
                            break
                    }
                })

                return allRulesValid
            })
        },
        shippingPoints() {
            return this.form.items.reduce((acc, item) => {
                const points = _.get(item, 'article.shipping_points')
                    || _.get(item, 'article.product.shipping_points')
                    || 0

                return acc + points
            }, 0)
        },
        customerGroup() {
            const customer = this.form.purchaser.customer
            return _.get(customer, 'customerGroup') || _.get(this.activeCms, 'shop.defaultCustomerGroup')
        },
        currency() {
            const currency = { code: 'EUR', sign: '€' }
            return _.get(this.customerGroup, 'currency') || currency
        },
        countries() {
            return countries.getNames('de')
        },
        ...mapGetters({
            options: 'ext_shop_purchase/options',

            // cms
            activeCms: 'activeCms'
            // enabledExtensions: 'enabledExtensions',
        })
    },
    methods: {
        findPrice(prices) {
            if (!prices) return

            let price;

            // const shopStore = useShopStore();
            // const customer = shopStore.customer;
            // const defaultCustomerGroup = shopStore.defaultCustomerGroup;

            const customerGroup = _.get(this.form.purchaser, 'customer.customerGroup')
            const defaultCustomerGroup = _.get(this.activeCms, 'shop.defaultCustomerGroup')

            let type = 'main';
            if (customerGroup && defaultCustomerGroup.id !== customerGroup.id) type = 'group';

            if (type === 'main') price = prices.find(el => el.type === type);
            else price = prices.find(el => customerGroup.id === _.get(el, 'customerGroup.id'));

            if (price && price.value) {
                return price.value;
            }
        },
        getSalutation(value) {
            switch (value) {
                case 'M':
                    return this.$t('Mr.')
                case 'F':
                    return this.$t('Mrs.')
                default:
                    return this.$t('Undefined')
            }
        },
    },
    mounted() {
        if (!this.form.purchaser.shippingAddress) {
            this.form.purchaser.shippingAddress = {
                id: undefined,
                gender: undefined,
                first_name: undefined,
                last_name: undefined,
                company: undefined,
                street: _.get(this.form.purchaser, 'billingAddress.street'),
                street_addition: undefined,
                zipcode: _.get(this.form.purchaser, 'billingAddress.zipcode'),
                city: _.get(this.form.purchaser, 'billingAddress.city'),
                country: _.get(this.form.purchaser, 'billingAddress.country'), // ISO-2 code
                type: 'shipping',
                latitude: undefined,
                longitude: undefined
            }
        }
    }
}
</script>

<style lang="sass" scoped>
h3
    font-weight: 300
    // text-align: center
.custom-option
    display: flex
    flex-direction: column
    // align-items: center
    justify-content: center
    min-height: 34px
    line-height: 1.2
</style>
